import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import i18n from '../i18n';
import { applyLocale, resolveLocale } from '../utils/locale';

export const AuthContext = createContext();

const syncLocale = (user) => {
  const locale = resolveLocale(user?.preferred_locale);
  applyLocale(locale);
  i18n.changeLanguage(locale);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/me');
      const nextUser = response.data.data;
      setUser(nextUser);
      syncLocale(nextUser);
    } catch (error) {
      console.error('Fetch user error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { user: nextUser, token: nextToken } = response.data.data;
      setToken(nextToken);
      setUser(nextUser);
      localStorage.setItem('token', nextToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
      syncLocale(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { user: nextUser, token: nextToken } = response.data.data;
      setToken(nextToken);
      setUser(nextUser);
      localStorage.setItem('token', nextToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
      syncLocale(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const loginWithGoogle = async (credential, invitationToken) => {
    try {
      const payload = { credential };
      if (invitationToken) {
        payload.invitationToken = invitationToken;
      }
      const response = await axios.post('/api/auth/google', payload);
      const { user: nextUser, token: nextToken } = response.data.data;
      setToken(nextToken);
      setUser(nextUser);
      localStorage.setItem('token', nextToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
      syncLocale(nextUser);
      return { success: true, user: nextUser };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Google login failed'
      };
    }
  };

  const logout = async () => {
    try {
      if (localStorage.getItem('token')) {
        await axios.post('/api/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
